import { useCallback, useEffect, useMemo, useState } from "react";
import { PlusCircle, Trash2, X } from "lucide-react";
import { StandardModal } from "@/components/StandardModal";
import { ModalBackdrop } from "@/components/ModalBackdrop";
import { ModalPrimaryAction } from "@/components/ModalPrimaryAction";
import { Autocomplete, type AutocompleteOption } from "@/components/ui/autocomplete";
import { FilterSelect } from "@/components/ui/filter-select";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  MODAL_BODY_CLASS,
  MODAL_HEADER_ROW_CLASS,
  MODAL_PANEL_CLASS,
  MODAL_PANEL_MAX_HEIGHT_CLASS,
  MODAL_TITLE_CLASS,
  TABLE_HEAD_CLASS,
  TABLE_TH_CLASS,
} from "@/lib/modalStyles";
import { api, type AiStockAnalyzedItem, type Insumo, type Produto } from "@/lib/api";
import { maskMoneyInput, numberToMoneyMask, parseMoneyMask } from "@/lib/moneyMask";
import { unidadeCanonicaParaBase } from "@/lib/unidades";
import { toast } from "@/lib/appToast";
import { formatDisplayText } from "@/lib/formatDisplayText";

const UNIDADES = ["un", "kg", "g", "L", "ml"] as const;
const PRODUTO_UNIDADES_VENDA = [
  { value: "unidade", label: "Unidade" },
  { value: "quilo", label: "Kg" },
  { value: "grama", label: "Gramas" },
  { value: "litro", label: "Litro" },
  { value: "mililitro", label: "Mililitro" },
] as const;

export type AiStockReviewRow = {
  key: string;
  nome_lido: string;
  item_tipo: "produto" | "insumo" | null;
  item_id: number | null;
  item_nome: string;
  item_unidade: string | null;
  unidade_lida: string;
  quantidade: string;
  valor_pago: string;
  status: "identificado" | "revisar" | "nao_encontrado";
  /** Match existe, mas o usuário ainda precisa confirmar/corrigir algum dado. */
  requires_review: boolean;
};

function unitsCompatible(lida: string, base: string): boolean {
  const a = unidadeCanonicaParaBase(lida);
  const b = unidadeCanonicaParaBase(base);
  if (a === b) return true;
  const peso = new Set(["kg", "g"]);
  const vol = new Set(["L", "ml"]);
  return (peso.has(a) && peso.has(b)) || (vol.has(a) && vol.has(b));
}

function recomputeStatus(row: AiStockReviewRow): AiStockReviewRow["status"] {
  if (!row.item_id || !row.item_tipo) return "nao_encontrado";
  if (row.requires_review) return "revisar";
  if (!row.unidade_lida || !UNIDADES.includes(row.unidade_lida as (typeof UNIDADES)[number])) {
    return "revisar";
  }
  if (row.item_unidade && !unitsCompatible(row.unidade_lida, row.item_unidade)) {
    return "revisar";
  }
  const qtd = parseFloat(row.quantidade.replace(",", "."));
  if (!(qtd > 0)) return "revisar";
  if (row.valor_pago.trim() === "") return "revisar";
  const valorPago = parseMoneyMask(row.valor_pago);
  if (!Number.isFinite(valorPago) || valorPago < 0) return "revisar";
  return "identificado";
}

function unitOptionsForRow(row: AiStockReviewRow): readonly string[] {
  if (!row.item_id || !row.item_unidade) return UNIDADES;
  const base = unidadeCanonicaParaBase(row.item_unidade);
  const family =
    base === "kg" || base === "g"
      ? ["kg", "g"]
      : base === "L" || base === "ml"
        ? ["L", "ml"]
        : ["un"];
  if (family.includes(row.unidade_lida)) {
    return [row.unidade_lida, ...family.filter((u) => u !== row.unidade_lida)];
  }
  return family;
}

/*
function statusBadge(status: AiStockReviewRow["status"]) {
  if (status === "identificado") {
    return (
      <span className="inline-flex rounded-md bg-[#E6F4EA] px-2 py-0.5 text-xs font-medium text-[#1E8E3E]">
        Identificado
      </span>
    );
  }
  if (status === "revisar") {
    return (
      <span className="inline-flex rounded-md bg-[#FEF7E0] px-2 py-0.5 text-xs font-medium text-[#D97706]">
        Revisar
      </span>
    );
  }
  return (
    <span className="inline-flex rounded-md bg-[#FCE8E6] px-2 py-0.5 text-xs font-medium text-[#D93025]">
      Não encontrado
    </span>
  );
}
*/

function statusDotClass(status: AiStockReviewRow["status"]): string {
  if (status === "identificado") return "bg-[#1E8E3E]";
  if (status === "revisar") return "bg-[#D97706]";
  return "bg-[#D93025]";
}

function unidadeLidaParaProdutoVenda(unidade: string): string {
  const base = unidadeCanonicaParaBase(unidade);
  if (base === "kg") return "quilo";
  if (base === "g") return "grama";
  if (base === "L") return "litro";
  if (base === "ml") return "mililitro";
  if (base === "un") return "unidade";
  return "";
}

function unidadeLidaParaInsumo(unidade: string): string {
  const base = unidadeCanonicaParaBase(unidade);
  return UNIDADES.includes(base as (typeof UNIDADES)[number]) ? base : "";
}

type QuickCreateType = "insumo" | "produto";

type QuickCreateState = {
  rowKey: string;
  tipo: QuickCreateType;
  nome: string;
  categoria: string;
  unidade: string;
  custo: string;
  estoqueMinimo: string;
  precoVenda: string;
  quantidadeMinima: string;
};

function buildQuickCreateState(row: AiStockReviewRow): QuickCreateState {
  const nome = formatDisplayText(row.item_nome || row.nome_lido);
  const unidadeBase = row.unidade_lida || row.item_unidade || "";
  return {
    rowKey: row.key,
    tipo: "insumo",
    nome,
    categoria: "",
    unidade: unidadeLidaParaInsumo(unidadeBase),
    custo: row.valor_pago,
    estoqueMinimo: "0",
    precoVenda: "",
    quantidadeMinima: "0",
  };
}

function toRows(itens: AiStockAnalyzedItem[]): AiStockReviewRow[] {
  return itens.map((it, idx) => {
    const qtd = it.quantidade != null && it.quantidade > 0 ? String(it.quantidade) : "";
    const valorPago =
      it.valor_pago != null && it.valor_pago >= 0 ? numberToMoneyMask(it.valor_pago) : "";
    const row: AiStockReviewRow = {
      key: `ai-${idx}-${it.nome_lido}`,
      nome_lido: it.nome_lido,
      item_tipo: it.item_tipo,
      item_id: it.item_id,
      item_nome: formatDisplayText(it.item_nome),
      item_unidade: it.item_unidade,
      unidade_lida: it.unidade_lida ?? "",
      quantidade: qtd,
      valor_pago: valorPago,
      status: it.status,
      requires_review: it.status === "revisar",
    };
    if (row.item_id && row.item_unidade) {
      const unidadeValida = UNIDADES.includes(row.unidade_lida as (typeof UNIDADES)[number]);
      if (!unidadeValida || !unitsCompatible(row.unidade_lida, row.item_unidade)) {
        // Item continua identificado, mas usa a única/família válida do cadastro.
        row.unidade_lida = unidadeCanonicaParaBase(row.item_unidade);
        row.requires_review = true;
      }
    }
    row.status = recomputeStatus(row);
    return row;
  });
}

type Props = {
  open: boolean;
  itens: AiStockAnalyzedItem[];
  sessionId?: number | null;
  onClose: () => void;
  onSuccess: () => void;
};

export function ModalAiStockReview({ open, itens, sessionId, onClose, onSuccess }: Props) {
  const [rows, setRows] = useState<AiStockReviewRow[]>([]);
  const [options, setOptions] = useState<AutocompleteOption[]>([]);
  const [candidatesMeta, setCandidatesMeta] = useState<
    Record<string, { tipo: "produto" | "insumo"; id: number; unidade: string }>
  >({});
  const [saving, setSaving] = useState(false);
  const [loadingOpts, setLoadingOpts] = useState(false);
  const [categorias, setCategorias] = useState<{ id: number; nome: string }[]>([]);
  const [quickCreate, setQuickCreate] = useState<QuickCreateState | null>(null);
  const [quickSaving, setQuickSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setRows(toRows(itens));
    setQuickCreate(null);
  }, [open, itens]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      try {
        const list = await api.listarCategorias();
        if (!cancelled) setCategorias(list);
      } catch {
        if (!cancelled) toast.error("Não foi possível carregar as categorias.");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      setLoadingOpts(true);
      try {
        const [prods, insumos] = await Promise.all([
          api.listarProdutosSemComposicao(),
          api.listarInsumos(),
        ]);
        if (cancelled) return;
        const meta: Record<string, { tipo: "produto" | "insumo"; id: number; unidade: string }> = {};
        const opts: AutocompleteOption[] = [];
        (prods as Produto[]).forEach((p) => {
          if (p.controla_estoque === false) return;
          const key = `produto:${p.id}`;
          meta[key] = {
            tipo: "produto",
            id: p.id,
            unidade: unidadeCanonicaParaBase(p.unidade_venda ?? p.unidade ?? "un"),
          };
          opts.push({ value: key, label: formatDisplayText(p.nome), meta: "Produto" });
        });
        (insumos as Insumo[]).forEach((ins) => {
          const key = `insumo:${ins.id}`;
          meta[key] = { tipo: "insumo", id: ins.id, unidade: unidadeCanonicaParaBase(ins.unidade ?? "un") };
          opts.push({ value: key, label: formatDisplayText(ins.nome), meta: "Insumo" });
        });
        setCandidatesMeta(meta);
        setOptions(opts);
      } catch {
        toast.error("Não foi possível carregar os itens do estoque.");
      } finally {
        if (!cancelled) setLoadingOpts(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open]);

  const summary = useMemo(() => {
    const lidos = rows.length;
    let idn = 0;
    let rev = 0;
    let nao = 0;
    for (const r of rows) {
      if (r.status === "identificado") idn += 1;
      else if (r.status === "revisar") rev += 1;
      else nao += 1;
    }
    return { lidos, idn, rev, nao };
  }, [rows]);

  const allValid = useMemo(() => {
    if (rows.length === 0) return false;
    return rows.every((r) => recomputeStatus(r) === "identificado");
  }, [rows]);

  const updateRow = useCallback((key: string, patch: Partial<AiStockReviewRow>) => {
    setRows((prev) =>
      prev.map((r) => {
        if (r.key !== key) return r;
        const next = { ...r, ...patch };
        next.status = recomputeStatus(next);
        return next;
      }),
    );
  }, []);

  const handleOpenQuickCreate = useCallback((row: AiStockReviewRow) => {
    setQuickCreate(buildQuickCreateState(row));
  }, []);

  const handleQuickTypeChange = useCallback((tipo: QuickCreateType) => {
    setQuickCreate((prev) => {
      if (!prev || prev.tipo === tipo) return prev;
      const row = rows.find((r) => r.key === prev.rowKey);
      const unidadeBase = row?.unidade_lida || row?.item_unidade || "";
      return {
        ...prev,
        tipo,
        unidade: tipo === "insumo" ? unidadeLidaParaInsumo(unidadeBase) : unidadeLidaParaProdutoVenda(unidadeBase),
      };
    });
  }, [rows]);

  const handleQuickCreate = async () => {
    if (!quickCreate || quickSaving) return;
    const nome = quickCreate.nome.trim();
    const categoria = quickCreate.categoria.trim();
    const unidade = quickCreate.unidade.trim();
    if (!nome || !categoria || !unidade) {
      toast.error("Preencha os campos obrigatórios.");
      return;
    }

    setQuickSaving(true);
    try {
      const custo = quickCreate.custo.trim() ? parseMoneyMask(quickCreate.custo) : null;
      if (quickCreate.tipo === "insumo") {
        const insumo = await api.criarInsumo({
          nome,
          categoria,
          unidade,
          estoqueMinimo: Number(quickCreate.estoqueMinimo.replace(",", ".")) || 0,
          valor_atual: custo,
        });
        const optionKey = `insumo:${insumo.id}`;
        const unidadeBase = unidadeCanonicaParaBase(insumo.unidade ?? unidade);
        setCandidatesMeta((prev) => ({
          ...prev,
          [optionKey]: { tipo: "insumo", id: insumo.id, unidade: unidadeBase },
        }));
        setOptions((prev) => [
          ...prev,
          { value: optionKey, label: formatDisplayText(insumo.nome), meta: "Insumo" },
        ]);
        updateRow(quickCreate.rowKey, {
          item_tipo: "insumo",
          item_id: insumo.id,
          item_nome: formatDisplayText(insumo.nome),
          item_unidade: unidadeBase,
          requires_review: true,
        });
      } else {
        const produto = await api.criarProduto({
          nome,
          categoria,
          possui_composicao: false,
          controla_estoque: true,
          exibir_no_cardapio: true,
          unidade_venda: unidade,
          preco_venda: quickCreate.precoVenda.trim() ? parseMoneyMask(quickCreate.precoVenda) : null,
          quantidade_minima: Number(quickCreate.quantidadeMinima.replace(",", ".")) || 0,
          valor_atual: custo,
          estoque_inicial: 0,
        });
        const optionKey = `produto:${produto.id}`;
        const unidadeBase = unidadeCanonicaParaBase(produto.unidade_venda ?? produto.unidade ?? unidade);
        setCandidatesMeta((prev) => ({
          ...prev,
          [optionKey]: { tipo: "produto", id: produto.id, unidade: unidadeBase },
        }));
        setOptions((prev) => [
          ...prev,
          { value: optionKey, label: formatDisplayText(produto.nome), meta: "Produto" },
        ]);
        updateRow(quickCreate.rowKey, {
          item_tipo: "produto",
          item_id: produto.id,
          item_nome: formatDisplayText(produto.nome),
          item_unidade: unidadeBase,
          requires_review: true,
        });
      }
      toast.success("Item criado com sucesso.");
      setQuickCreate(null);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Não foi possível criar o item.");
    } finally {
      setQuickSaving(false);
    }
  };

  const handleConfirm = async () => {
    if (!allValid || saving) return;
    setSaving(true);
    try {
      const payload = rows.map((r) => {
        const qtd = parseFloat(r.quantidade.replace(",", "."));
        const valorPago = parseMoneyMask(r.valor_pago);
        return {
          nome_lido: r.nome_lido,
          item_tipo: r.item_tipo as "produto" | "insumo",
          item_id: r.item_id as number,
          quantidade: qtd,
          unidade_lida: r.unidade_lida,
          valor_pago: valorPago,
        };
      });
      await api.aiStockConfirmEntradas(payload, sessionId);
      toast.success("Entradas registradas com sucesso.");
      onSuccess();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Não foi possível confirmar as entradas.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <StandardModal
        open={open}
        title="Revisar itens"
        onClose={onClose}
        closeDisabled={saving}
        panelClassName={`!max-w-4xl ${MODAL_PANEL_MAX_HEIGHT_CLASS} !overflow-hidden`}
      >
      <div className={`${MODAL_BODY_CLASS} min-h-0 flex-1 overflow-y-auto overflow-x-hidden`}>
        <div className="flex w-full flex-wrap gap-4 text-sm rounded-lg border border-gray-100 bg-gray-50 px-3 py-2">
          <span>
            <strong>{summary.lidos}</strong> itens lidos
          </span>
          <span className="text-[#1E8E3E]">
            <strong>{summary.idn}</strong> identificados
          </span>
          <span className="text-[#D97706]">
            <strong>{summary.rev}</strong> revisar
          </span>
          <span className="text-[#D93025]">
            <strong>{summary.nao}</strong> não encontrado
          </span>
        </div>

        <div className="overflow-x-auto rounded-md border border-gray-200 lg:overflow-x-visible">
          <table className="gs-management-table gs-management-table-specialized w-full min-w-[620px] table-fixed text-sm lg:min-w-0">
            <thead className={TABLE_HEAD_CLASS}>
              <tr>
                <th className={`w-[19%] whitespace-nowrap px-2 py-2 text-left ${TABLE_TH_CLASS}`}>Leitura</th>
                <th className={`w-[27%] whitespace-nowrap px-2 py-2 text-left ${TABLE_TH_CLASS}`}>
                  Item
                </th>
                <th className={`w-[9%] whitespace-nowrap px-1.5 py-2 text-left ${TABLE_TH_CLASS}`}>Qtd.</th>
                <th className={`w-[10%] whitespace-nowrap px-1.5 py-2 text-left ${TABLE_TH_CLASS}`}>Unid.</th>
                <th className={`w-[14%] whitespace-nowrap px-1.5 py-2 text-left ${TABLE_TH_CLASS}`}>Valor pago</th>
                <th className={`w-[7%] whitespace-nowrap px-1.5 py-2 text-left ${TABLE_TH_CLASS}`}>Status</th>
                <th className={`w-[12%] whitespace-nowrap px-1.5 py-2 ${TABLE_TH_CLASS}`} aria-label="Ação" />
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-3 py-8 text-center text-muted-foreground">
                    Nenhum item para revisar.
                  </td>
                </tr>
              ) : (
                rows.map((r) => {
                  const itemUnit = r.item_unidade
                    ? unidadeCanonicaParaBase(r.item_unidade)
                    : null;
                  return (
                    <tr key={r.key} className="border-t border-gray-100 align-middle">
                      <td className="px-2 py-2 align-middle font-medium text-foreground">
                        <span className="block truncate whitespace-nowrap" title={formatDisplayText(r.nome_lido)}>
                          {formatDisplayText(r.nome_lido)}
                        </span>
                      </td>
                      <td className="px-2 py-2 align-middle">
                        <Autocomplete
                          options={options}
                          value={r.item_nome}
                          onValueChange={(v) =>
                            updateRow(r.key, {
                              item_nome: v,
                              item_id: null,
                              item_tipo: null,
                              item_unidade: null,
                              requires_review: false,
                            })
                          }
                          onSelect={(opt) => {
                            const meta = candidatesMeta[String(opt.value)];
                            if (!meta) return;
                            const currentUnitValid = UNIDADES.includes(
                              r.unidade_lida as (typeof UNIDADES)[number],
                            );
                            const compatible =
                              currentUnitValid && unitsCompatible(r.unidade_lida, meta.unidade);
                            updateRow(r.key, {
                              item_nome: opt.label,
                              item_id: meta.id,
                              item_tipo: meta.tipo,
                              item_unidade: meta.unidade,
                              unidade_lida: compatible
                                ? r.unidade_lida
                                : unidadeCanonicaParaBase(meta.unidade),
                              requires_review: !compatible,
                            });
                          }}
                          placeholder={loadingOpts ? "Carregando…" : "Buscar item…"}
                          requireQueryForSuggestions
                          maxVisibleItems={4}
                          portal
                          className="h-8 text-sm"
                        />
                      </td>
                      <td className="px-1.5 py-2 align-middle">
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          value={r.quantidade}
                          onChange={(e) =>
                            updateRow(r.key, {
                              quantidade: e.target.value,
                              requires_review: false,
                            })
                          }
                          className="h-8 w-full min-w-[4rem] px-2 text-sm tabular-nums"
                        />
                      </td>
                      <td className="whitespace-nowrap px-1.5 py-2 align-middle">
                        {r.item_id && itemUnit === "un" ? (
                          <span className="inline-flex h-8 min-w-[3.5rem] items-center justify-center text-sm font-medium text-foreground">
                            un
                          </span>
                        ) : (
                          <FilterSelect
                            size="compact"
                            value={r.unidade_lida}
                            placeholder="Selecione..."
                            aria-label="Unidade"
                            onChange={(e) =>
                              updateRow(r.key, {
                                unidade_lida: e.target.value,
                                requires_review: false,
                              })
                            }
                            className="h-8 w-full min-w-[3.75rem]"
                          >
                            <option value="">Selecione...</option>
                            {unitOptionsForRow(r).map((u) => (
                              <option key={u} value={u}>
                                {u}
                              </option>
                            ))}
                          </FilterSelect>
                        )}
                      </td>
                      <td className="px-1.5 py-2 align-middle">
                        <div className="flex h-8 w-full min-w-[5.75rem] overflow-hidden rounded-md border border-input bg-background text-sm focus-within:outline-none">
                          <span
                            className="flex h-full shrink-0 items-center justify-center border-r border-input bg-muted text-[inherit] text-foreground"
                            style={{ flex: "0 0 36px" }}
                          >
                            R$
                          </span>
                          <input
                            type="text"
                            inputMode="numeric"
                            autoComplete="off"
                            value={r.valor_pago}
                            onChange={(e) =>
                              updateRow(r.key, {
                                valor_pago: maskMoneyInput(e.target.value),
                                requires_review: false,
                              })
                            }
                            className="min-w-0 flex-1 bg-transparent px-2 py-2 text-left text-[inherit] text-foreground outline-none placeholder:text-muted-foreground"
                          />
                        </div>
                      </td>
                      <td className="whitespace-nowrap px-1.5 py-2 align-middle">
                        <div className="flex items-center justify-center">
                          <span
                            className={`h-3 w-3 rounded-full ${statusDotClass(r.status)}`}
                            aria-label={r.status}
                            title={r.status}
                          />
                        </div>
                      </td>
                      <td className="px-1.5 py-2 text-center align-middle">
                        <div className="flex items-center justify-center gap-2">
                          <span className="inline-flex h-8 w-8 items-center justify-center">
                            {r.status === "nao_encontrado" ? (
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 text-foreground hover:text-foreground"
                                aria-label="Adicionar item"
                                onClick={() => handleOpenQuickCreate(r)}
                              >
                                <PlusCircle className="h-4 w-4" />
                              </Button>
                            ) : null}
                          </span>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-destructive hover:text-destructive"
                            aria-label="Remover linha"
                            onClick={() => {
                              if (rows.length <= 1) {
                                onClose();
                                return;
                              }
                              setRows((prev) => prev.filter((x) => x.key !== r.key));
                            }}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        <ModalPrimaryAction
          show={allValid}
          type="button"
          loading={saving}
          disabled={saving}
          onClick={() => void handleConfirm()}
        >
          Confirmar entradas
        </ModalPrimaryAction>
      </div>
      </StandardModal>

      <ModalBackdrop open={Boolean(quickCreate)} className="z-[120] bg-slate-900/45">
        {quickCreate ? (
          <div
            className={`relative flex w-full max-w-[31rem] flex-col overflow-hidden ${MODAL_PANEL_CLASS} p-0`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className={MODAL_HEADER_ROW_CLASS}>
              <div className="min-w-0">
                <h2 className={MODAL_TITLE_CLASS}>Adicionar item</h2>
                <p className="mt-2 text-sm font-normal text-slate-500">
                  Crie rapidamente um insumo ou produto a partir do item não encontrado.
                </p>
              </div>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-9 w-9 shrink-0"
                aria-label="Fechar"
                disabled={quickSaving}
                onClick={() => setQuickCreate(null)}
              >
                <X className="h-5 w-5" />
              </Button>
            </div>

            <div className="space-y-4 bg-white p-6 text-gray-900">
              <div className="space-y-2">
                <span className="text-sm font-semibold">Tipo *</span>
                <div className="grid grid-cols-2 gap-3">
                  {(["insumo", "produto"] as const).map((tipo) => (
                    <Button
                      key={tipo}
                      type="button"
                      variant={quickCreate.tipo === tipo ? "default" : "outline"}
                      className={`h-11 ${quickCreate.tipo === tipo ? "bg-slate-950 text-white hover:bg-slate-900" : "bg-white"}`}
                      onClick={() => handleQuickTypeChange(tipo)}
                      disabled={quickSaving}
                    >
                      {tipo === "insumo" ? "Insumo" : "Produto"}
                    </Button>
                  ))}
                </div>
              </div>

              <div className="space-y-2">
                <label htmlFor="quick-item-nome" className="text-sm font-semibold">
                  {quickCreate.tipo === "insumo" ? "Nome do Insumo *" : "Nome *"}
                </label>
                <Input
                  id="quick-item-nome"
                  value={quickCreate.nome}
                  onChange={(e) => setQuickCreate((prev) => (prev ? { ...prev, nome: e.target.value } : prev))}
                />
              </div>

              <div className="space-y-2">
                <label htmlFor="quick-item-categoria" className="text-sm font-semibold">
                  Categoria *
                </label>
                <FilterSelect
                  id="quick-item-categoria"
                  value={quickCreate.categoria}
                  onChange={(e) => setQuickCreate((prev) => (prev ? { ...prev, categoria: e.target.value } : prev))}
                >
                  <option value="">Selecione a categoria</option>
                  {categorias.map((cat) => (
                    <option key={cat.id} value={cat.nome}>
                      {cat.nome}
                    </option>
                  ))}
                </FilterSelect>
              </div>

              {quickCreate.tipo === "produto" ? (
                <div className="space-y-2">
                  <label htmlFor="quick-item-preco" className="text-sm font-semibold">
                    Preço de venda
                  </label>
                  <Input
                    id="quick-item-preco"
                    inputMode="numeric"
                    value={quickCreate.precoVenda}
                    onChange={(e) =>
                      setQuickCreate((prev) => (prev ? { ...prev, precoVenda: maskMoneyInput(e.target.value) } : prev))
                    }
                  />
                </div>
              ) : null}

              <div className="space-y-2">
                <label htmlFor="quick-item-custo" className="text-sm font-semibold">
                  Custo
                </label>
                <Input
                  id="quick-item-custo"
                  inputMode="numeric"
                  value={quickCreate.custo}
                  onChange={(e) => setQuickCreate((prev) => (prev ? { ...prev, custo: maskMoneyInput(e.target.value) } : prev))}
                />
              </div>

              <div className="space-y-2">
                <label htmlFor="quick-item-unidade" className="text-sm font-semibold">
                  {quickCreate.tipo === "insumo" ? "Unidade de Medida *" : "Unidade de venda *"}
                </label>
                <FilterSelect
                  id="quick-item-unidade"
                  value={quickCreate.unidade}
                  onChange={(e) => setQuickCreate((prev) => (prev ? { ...prev, unidade: e.target.value } : prev))}
                >
                  <option value="">Selecione...</option>
                  {quickCreate.tipo === "insumo"
                    ? UNIDADES.map((u) => (
                        <option key={u} value={u}>
                          {u}
                        </option>
                      ))
                    : PRODUTO_UNIDADES_VENDA.map((u) => (
                        <option key={u.value} value={u.value}>
                          {u.label}
                        </option>
                      ))}
                </FilterSelect>
              </div>

              <div className="space-y-2">
                <label htmlFor="quick-item-minimo" className="text-sm font-semibold">
                  {quickCreate.tipo === "insumo" ? "Estoque Mínimo" : "Quantidade mínima"}
                </label>
                <Input
                  id="quick-item-minimo"
                  type="number"
                  min="0"
                  value={quickCreate.tipo === "insumo" ? quickCreate.estoqueMinimo : quickCreate.quantidadeMinima}
                  onChange={(e) =>
                    setQuickCreate((prev) =>
                      prev
                        ? quickCreate.tipo === "insumo"
                          ? { ...prev, estoqueMinimo: e.target.value }
                          : { ...prev, quantidadeMinima: e.target.value }
                        : prev,
                    )
                  }
                />
              </div>

              <div className="flex justify-end gap-3 pt-1">
                <Button type="button" variant="outline" onClick={() => setQuickCreate(null)} disabled={quickSaving}>
                  Cancelar
                </Button>
                <Button type="button" onClick={() => void handleQuickCreate()} disabled={quickSaving}>
                  {quickSaving ? "Criando..." : "Criar item"}
                </Button>
              </div>
            </div>
          </div>
        ) : null}
      </ModalBackdrop>
    </>
  );
}
